import React, { useEffect, useRef } from "react";
import { audioSupported, peekAnalyser } from "../../lib/synthLab";

const CAN_PLAY = audioSupported();

// Live oscilloscope: taps the synth's shared output via the AnalyserNode and
// draws the time-domain waveform, so the shape visibly changes as you tweak
// the filter/envelope or hold notes. Renders a flat baseline until the first
// note brings the audio bus to life.
const Scope = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!CAN_PLAY) return undefined;
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const c = canvas.getContext("2d");
    let raf;
    let buf;

    const draw = () => {
      raf = requestAnimationFrame(draw);
      const dpr = window.devicePixelRatio || 1;
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) return;
      if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
        canvas.width = Math.round(w * dpr);
        canvas.height = Math.round(h * dpr);
      }
      c.setTransform(dpr, 0, 0, dpr, 0, 0);
      c.clearRect(0, 0, w, h);

      // center baseline
      c.strokeStyle = "rgba(130,130,150,0.25)";
      c.lineWidth = 1;
      c.beginPath();
      c.moveTo(0, h / 2);
      c.lineTo(w, h / 2);
      c.stroke();

      const analyser = peekAnalyser();
      if (!analyser) return; // no sound yet → just the baseline
      const n = analyser.fftSize;
      if (!buf || buf.length !== n) buf = new Uint8Array(n);
      analyser.getByteTimeDomainData(buf);

      c.strokeStyle = getComputedStyle(canvas).color || "#caa";
      c.lineWidth = 2;
      c.lineJoin = "round";
      c.beginPath();
      for (let i = 0; i < n; i++) {
        const x = (i / (n - 1)) * w;
        const y = (buf[i] / 255) * h;
        if (i === 0) c.moveTo(x, y);
        else c.lineTo(x, y);
      }
      c.stroke();
    };

    draw();
    return () => cancelAnimationFrame(raf);
  }, []);

  if (!CAN_PLAY) return null;
  return (
    <canvas
      ref={canvasRef}
      className="h-16 w-full text-primary"
      aria-label="Oscilloscope showing the live output waveform"
    />
  );
};

export default Scope;
